import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import Card from '../components/common/Card';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const OrderFormPage = () => {
  const {
    API, accountSettings, activeTab, editingOrderForm, newOrderForm, orderForms, products,
    setAccountSettings, setActiveTab, setEditingOrderForm, setNewOrderForm, setOrderForms,
    setShowOrderFormBuilder, showConfirm, showOrderFormBuilder, showToast, user,
  } = useDashboard();

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-6 md:px-8 lg:px-12">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Order Form</h2>
      </div>
      
      {/* Public Booking Link Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Your Booking Link</h3>
              <p className="text-blue-100 text-sm">Share this link with clients to book appointments</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Default Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Booking URL</label>
            <p className="text-xs text-gray-500 mb-2">This link always works, even if you set up a custom domain</p>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="text"
                readOnly
                value={`https://est8.media/b/${user?.business_id || ''}`}
                className="flex-1 bg-transparent text-gray-700 text-sm font-mono outline-none"
              />
              <button
                onClick={async () => {
                  const url = `https://est8.media/b/${user?.business_id || ''}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    showToast('Link copied to clipboard!', 'success');
                  } catch (error) {
                    const textArea = document.createElement('textarea');
                    textArea.value = url;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    showToast('Link copied to clipboard!', 'success');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <a
              href={accountSettings.booking_subdomain ? `/b/${accountSettings.booking_subdomain}` : `/b/${user?.business_id || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview Booking Page
            </a>
          </div>
        </div>
      </div>

      {/* White Label Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">White Label Settings</h3>
              <p className="text-sm text-gray-500">Customize your booking page branding</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Business Logo Display */}
          {accountSettings.logo_url && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <img src={accountSettings.logo_url.startsWith('http') ? accountSettings.logo_url : `${API.replace('/api', '')}${accountSettings.logo_url}`} alt="Logo" className="h-10 object-contain" />
              <span className="text-sm text-gray-600">Your logo will appear on the booking page</span>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('settings'); }} className="ml-auto text-sm text-purple-600 hover:text-purple-700">
                Change in Settings
              </a>
            </div>
          )}
          {!accountSettings.logo_url && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm text-amber-800">No logo uploaded</span>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('settings'); }} className="ml-auto text-sm text-amber-700 hover:text-amber-800 font-medium">
                Upload in Settings → Account
              </a>
            </div>
          )}

          {/* Custom Booking Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Booking URL</label>
            <p className="text-xs text-gray-500 mb-3">Create a memorable booking link for your business</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center">
                <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-500 text-sm">{window.location.origin}/b/</span>
                <input
                  type="text"
                  placeholder="yourbrand"
                  value={accountSettings.booking_subdomain || ''}
                  onChange={(e) => setAccountSettings(prev => ({ 
                    ...prev, 
                    booking_subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 30)
                  }))}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <button
                onClick={async () => {
                  if (!accountSettings.booking_subdomain) {
                    showToast('Please enter a custom name', 'error');
                    return;
                  }
                  try {
                    const token = localStorage.getItem('token');
                    const checkRes = await axios.get(`${API}/public/check-subdomain/${accountSettings.booking_subdomain}`);
                    if (!checkRes.data.available) {
                      showToast(checkRes.data.reason || 'Name not available', 'error');
                      return;
                    }
                    await axios.put(`${API}/settings/account`, {
                      booking_subdomain: accountSettings.booking_subdomain
                    }, { headers: { Authorization: `Bearer ${token}` }});
                    showToast('Booking URL saved!', 'success');
                  } catch (err) {
                    showToast(err.response?.data?.reason || 'Failed to save', 'error');
                  }
                }}
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                Save
              </button>
            </div>
            {accountSettings.booking_subdomain && (
              <div className="mt-3 flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <code className="flex-1 text-sm text-purple-700 font-mono">{window.location.origin}/b/{accountSettings.booking_subdomain}</code>
                <button
                  onClick={async () => {
                    const url = `${window.location.origin}/b/${accountSettings.booking_subdomain}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      showToast('Link copied!', 'success');
                    } catch (error) {
                      const textArea = document.createElement('textarea');
                      textArea.value = url;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      showToast('Link copied!', 'success');
                    }
                  }}
                  className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-colors"
                >
                  Copy
                </button>
              </div>
            )}
          </div>

          {/* Brand Color */}
          <div className="pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">Brand Color</label>
            <p className="text-xs text-gray-500 mb-3">Used for buttons and accents on your booking page</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={accountSettings.brand_color || '#3B82F6'}
                onChange={(e) => setAccountSettings(prev => ({ ...prev, brand_color: e.target.value }))}
                className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={accountSettings.brand_color || '#3B82F6'}
                onChange={(e) => setAccountSettings(prev => ({ ...prev, brand_color: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-28 font-mono"
              />
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(`${API}/settings/account`, { brand_color: accountSettings.brand_color }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    showToast('Brand color saved!', 'success');
                  } catch (err) {
                    showToast('Failed to save', 'error');
                  }
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Save Color
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Settings Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Form Settings</h3>
          <p className="text-sm text-gray-500 mt-1">Configure how clients can book appointments</p>
        </div>
        <div className="p-6 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Enable Online Booking</h4>
              <p className="text-sm text-gray-500">Allow clients to book appointments through the public form</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  await axios.patch(`${API}/settings/business`, {
                    public_booking_enabled: !accountSettings.public_booking_enabled
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setAccountSettings(prev => ({
                    ...prev,
                    public_booking_enabled: !prev.public_booking_enabled
                  }));
                } catch (err) {
                  console.error('Failed to update setting:', err);
                }
              }}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                accountSettings.public_booking_enabled !== false ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                  accountSettings.public_booking_enabled !== false ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Payment Setting */}
          <div className="pt-4 border-t border-gray-100">
            <div className="mb-3">
              <h4 className="font-medium text-gray-900">Payment at Booking</h4>
              <p className="text-sm text-gray-500">How much clients are charged when they book an appointment.</p>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="payment-mode-selector">
              {[
                { value: 'none', label: 'Off', desc: 'Invoice later' },
                { value: 'partial', label: 'Deposit', desc: `${accountSettings.deposit_percent_display || '25'}% upfront` },
                { value: 'full', label: 'Full', desc: 'Pay 100%' },
              ].map(opt => {
                const active = (accountSettings.payment_mode || 'none') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`payment-mode-${opt.value}`}
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(`${API}/settings/account`, { payment_mode: opt.value }, { headers: { Authorization: `Bearer ${token}` } });
                        setAccountSettings(prev => ({ ...prev, payment_mode: opt.value }));
                      } catch (err) {
                        console.error('Failed to update payment_mode:', err);
                      }
                    }}
                    className={`px-4 py-3 rounded-lg border text-left transition-colors ${active ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                  >
                    <div className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-gray-900'}`}>{opt.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2">Deposit % is configured in Settings → Integrations → Default Deposit %.</p>
          </div>

          {/* Payment Reminders */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900">Payment Reminders</h4>
                <p className="text-sm text-gray-500">Automatically email clients with outstanding balances ahead of their appointment.</p>
              </div>
              <button
                type="button"
                data-testid="payment-reminders-toggle"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const newVal = !(accountSettings.payment_reminders_enabled !== false);
                    await axios.put(`${API}/settings/account`, { payment_reminders_enabled: newVal }, { headers: { Authorization: `Bearer ${token}` } });
                    setAccountSettings(prev => ({ ...prev, payment_reminders_enabled: newVal }));
                  } catch (err) { console.error(err); }
                }}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${accountSettings.payment_reminders_enabled !== false ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${accountSettings.payment_reminders_enabled !== false ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>

            {accountSettings.payment_reminders_enabled !== false && (
              <div data-testid="payment-reminder-offsets">
                <label className="text-xs font-medium text-gray-600 mb-2 block">Send reminders this many days before each appointment:</label>
                <div className="flex flex-wrap gap-2">
                  {[14, 7, 3, 1, 0].map(d => {
                    const offsets = accountSettings.payment_reminder_offsets || [7, 3, 1];
                    const active = offsets.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`payment-reminder-offset-${d}`}
                        onClick={async () => {
                          const next = active ? offsets.filter(x => x !== d) : [...offsets, d].sort((a, b) => b - a);
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${API}/settings/account`, { payment_reminder_offsets: next }, { headers: { Authorization: `Bearer ${token}` } });
                            setAccountSettings(prev => ({ ...prev, payment_reminder_offsets: next }));
                          } catch (err) { console.error(err); }
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                      >
                        {d === 0 ? 'Day of' : d === 1 ? '1 day before' : `${d} days before`}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">Reminders are sent at most once per offset per appointment. Each send is logged on the appointment's activity feed.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Order Forms Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Custom Order Forms</h3>
            <p className="text-sm text-gray-500 mt-1">Create forms with custom questions that appear based on selected products</p>
          </div>
          <button
            onClick={() => {
              setEditingOrderForm(null);
              setNewOrderForm({
                name: '',
                description: '',
                product_ids: [],
                questions: [],
                is_active: true
              });
              setShowOrderFormBuilder(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Form
          </button>
        </div>
        <div className="p-6">
          {orderForms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No custom order forms yet</p>
              <p className="text-sm mt-1">Create forms with questions that appear when specific products are selected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orderForms.map((form) => {
                const linkedProducts = products.filter(p => form.product_ids?.includes(p.id));
                return (
                  <div 
                    key={form.id} 
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{form.name}</h4>
                          {form.is_active ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Inactive</span>
                          )}
                        </div>
                        {form.description && (
                          <p className="text-sm text-gray-500 mt-1">{form.description}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="text-xs text-gray-500">{form.questions?.length || 0} questions</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">Triggers: </span>
                          {linkedProducts.length > 0 ? (
                            linkedProducts.slice(0, 3).map(p => (
                              <span key={p.id} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                                {p.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No products linked</span>
                          )}
                          {linkedProducts.length > 3 && (
                            <span className="text-xs text-gray-500">+{linkedProducts.length - 3} more</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingOrderForm(form);
                            setNewOrderForm({
                              name: form.name,
                              description: form.description || '',
                              product_ids: form.product_ids || [],
                              questions: form.questions || [],
                              is_active: form.is_active !== false
                            });
                            setShowOrderFormBuilder(true);
                          }}
                          className="px-3 py-1.5 text-sm text-blue-400 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            showConfirm(
                              'Delete Order Form',
                              `Are you sure you want to delete "${form.name}"?`,
                              async () => {
                                try {
                                  await axios.delete(`${API}/settings/order-forms/${form.id}`);
                                  setOrderForms(orderForms.filter(f => f.id !== form.id));
                                  showToast('Order form deleted', 'success');
                                } catch (err) {
                                  showToast('Failed to delete order form', 'error');
                                }
                              }
                            );
                          }}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Order Form Builder Modal */}
      {showOrderFormBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 150 }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingOrderForm ? 'Edit Order Form' : 'Create Order Form'}
              </h3>
              <button onClick={() => setShowOrderFormBuilder(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Form Name *</label>
                    <input type="text" value={newOrderForm.name} onChange={(e) => setNewOrderForm({ ...newOrderForm, name: e.target.value })} placeholder="e.g., Property Details Form" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={newOrderForm.description} onChange={(e) => setNewOrderForm({ ...newOrderForm, description: e.target.value })} placeholder="Brief description..." rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div><label className="block text-sm font-medium text-gray-700">Active</label><p className="text-xs text-gray-500">Show on public booking page</p></div>
                    <button type="button" onClick={() => setNewOrderForm({ ...newOrderForm, is_active: !newOrderForm.is_active })} className={`relative inline-flex h-6 w-11 items-center rounded-full ${newOrderForm.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newOrderForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Products</label>
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      {products.filter(p => p.is_active !== false).map(product => (
                        <label key={product.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                          <input type="checkbox" checked={newOrderForm.product_ids.includes(product.id)} onChange={(e) => {
                            if (e.target.checked) { setNewOrderForm({ ...newOrderForm, product_ids: [...newOrderForm.product_ids, product.id] }); }
                            else { setNewOrderForm({ ...newOrderForm, product_ids: newOrderForm.product_ids.filter(id => id !== product.id) }); }
                          }} className="w-4 h-4 text-blue-400 rounded" />
                          <span className="text-sm text-gray-700">{product.name}</span>
                          <span className="text-xs text-gray-400 ml-auto">${product.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">Questions</label>
                    <button type="button" onClick={() => { setNewOrderForm({ ...newOrderForm, questions: [...newOrderForm.questions, { id: `q_${Date.now()}`, type: 'text', label: '', placeholder: '', required: false, options: [] }] }); }} className="text-sm text-blue-400 hover:text-blue-700 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      Add Question
                    </button>
                  </div>
                  {newOrderForm.questions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg"><p className="text-sm">No questions yet</p></div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {newOrderForm.questions.map((question, idx) => (
                        <div key={question.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex justify-between items-start gap-2 mb-2">
                            <span className="text-xs text-gray-500 font-medium">Question {idx + 1}</span>
                            <button type="button" onClick={() => setNewOrderForm({ ...newOrderForm, questions: newOrderForm.questions.filter(q => q.id !== question.id) })} className="text-red-500 hover:text-red-700">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                          <div className="space-y-2">
                            <input type="text" value={question.label} onChange={(e) => { const updated = newOrderForm.questions.map(q => q.id === question.id ? { ...q, label: e.target.value } : q); setNewOrderForm({ ...newOrderForm, questions: updated }); }} placeholder="Question label" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
                            <div className="flex gap-2">
                              <select value={question.type} onChange={(e) => { const updated = newOrderForm.questions.map(q => q.id === question.id ? { ...q, type: e.target.value } : q); setNewOrderForm({ ...newOrderForm, questions: updated }); }} className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded">
                                <option value="text">Text</option>
                                <option value="textarea">Long Text</option>
                                <option value="number">Number</option>
                                <option value="select">Dropdown</option>
                                <option value="radio">Radio</option>
                                <option value="checkbox">Checkboxes</option>
                                <option value="date">Date</option>
                                <option value="file">File Upload</option>
                              </select>
                              <label className="flex items-center gap-1 text-xs text-gray-600">
                                <input type="checkbox" checked={question.required} onChange={(e) => { const updated = newOrderForm.questions.map(q => q.id === question.id ? { ...q, required: e.target.checked } : q); setNewOrderForm({ ...newOrderForm, questions: updated }); }} className="w-3 h-3" />
                                Required
                              </label>
                            </div>
                            {['select', 'radio', 'checkbox'].includes(question.type) && (
                              <textarea value={(question.options || []).join('\n')} onChange={(e) => { const updated = newOrderForm.questions.map(q => q.id === question.id ? { ...q, options: e.target.value.split('\n') } : q); setNewOrderForm({ ...newOrderForm, questions: updated }); }} placeholder="Option 1&#10;Option 2" rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowOrderFormBuilder(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={async () => {
                if (!newOrderForm.name.trim()) { showToast('Please enter a form name', 'error'); return; }
                try {
                  if (editingOrderForm) {
                    const response = await axios.put(`${API}/settings/order-forms/${editingOrderForm.id}`, newOrderForm);
                    setOrderForms(orderForms.map(f => f.id === editingOrderForm.id ? response.data : f));
                    showToast('Order form updated', 'success');
                  } else {
                    const response = await axios.post(`${API}/settings/order-forms`, newOrderForm);
                    setOrderForms([...orderForms, response.data]);
                    showToast('Order form created', 'success');
                  }
                  setShowOrderFormBuilder(false);
                } catch (err) { showToast('Failed to save order form', 'error'); }
              }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                {editingOrderForm ? 'Save Changes' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrderFormPage;
